import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { PushNotifications } from "@capacitor/push-notifications";

/** True inside the iOS and Android apps (Capacitor), false in a browser. */
export const isNativeApp = Capacitor.isNativePlatform();

/**
 * True inside the iOS app. Apple requires its own in-app purchase for upgrades sold inside the app, so the iOS
 * app leaves out the links to the subscription page.
 */
export const isIosApp = Capacitor.getPlatform() === "ios";

/**
 * Lets the app's pages run under the status bar and home indicator, the way the iOS and Android web views draw
 * them. Without viewport-fit=cover iOS reports every safe area as 0, so the top bar ends up under the clock.
 * The stylesheet (css/components/safe-area.scss) then moves the fixed bars clear of the system bars.
 * Browsers keep the default viewport, so the web app is unchanged.
 */
export const enableEdgeToEdgeViewport = () => {
  if (!isNativeApp) {
    return;
  }
  const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (viewport && !viewport.content.includes("viewport-fit")) {
    viewport.content = `${viewport.content}, viewport-fit=cover`;
  }
};

/** Platform name in the form the device registration code expects ("Android" or "iOS"). */
export const nativePlatformName = Capacitor.getPlatform() === "android" ? "Android" : "iOS";

// Must match appId in capacitor.config.ts: it is also the URL scheme Auth0 sends the user back to.
const APP_ID = "com.upkeepr.upkeepr";

/**
 * Where Auth0 returns to after login or logout in the apps. The URL has to be listed under Allowed Callback
 * URLs and Allowed Logout URLs of the Auth0 Native application.
 */
export const nativeAuthCallbackUrl = (auth0Domain: string) =>
  `${APP_ID}://${auth0Domain}/capacitor/${APP_ID}/callback`;

/**
 * Opens Auth0's login or logout page in the system browser (SFSafariViewController / Custom Tabs). Auth0 and
 * Google refuse logins inside an embedded web view, and the system browser keeps saved passwords working.
 */
export const openInSystemBrowser = async (url: string) => {
  await Browser.open({ url, windowName: "_self" });
};

/** Calls onAuthCallback with every Auth0 callback URL the app is opened with, and closes the browser. */
export const listenForAuthCallbacks = (onAuthCallback: (url: string) => Promise<void>) => {
  const listener = App.addListener("appUrlOpen", async ({ url }) => {
    if (!url.startsWith(`${APP_ID}://`)) {
      return;
    }
    try {
      if (url.includes("state=") && (url.includes("code=") || url.includes("error="))) {
        await onAuthCallback(url);
      }
    } finally {
      await Browser.close().catch(() => undefined);
    }
  });
  return () => {
    listener.then((handle) => handle.remove());
  };
};

/**
 * Asks for permission to send notifications and registers the device with APNs or FCM. onToken gets the device
 * token, which the API stores and Azure Notification Hubs sends to. onOpen gets the in-app path of a tapped
 * notification.
 *
 * Android needs android/app/google-services.json from the Firebase project before this can run, so the apps
 * only call it when built with REACT_APP_NATIVE_PUSH=true.
 */
export const registerForPush = async (
  onToken: (token: string) => void,
  onOpen: (path: string) => void,
) => {
  await PushNotifications.addListener("registration", ({ value }) => onToken(value));
  await PushNotifications.addListener("registrationError", (error) =>
    console.log("Push registration failed", error),
  );
  await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
    // The API puts the full web app URL of the activity or invitation in data.Uri.
    const uri = notification.data?.Uri ?? notification.data?.data?.Uri;
    if (typeof uri === "string") {
      try {
        const { pathname, search, hash } = new URL(uri);
        onOpen(pathname + search + hash);
      } catch {
        // Not a URL; leave the app where it is.
      }
    }
  });

  let { receive } = await PushNotifications.checkPermissions();
  if (receive === "prompt" || receive === "prompt-with-rationale") {
    ({ receive } = await PushNotifications.requestPermissions());
  }
  if (receive === "granted") {
    await PushNotifications.register();
  }
};
